import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Scale } from "lucide-react";
import { Button } from "@/components/ui/button";
import { MP_COMPARISONS } from "@/lib/mp-compare";
import { MP_GUIDES } from "@/lib/mp-guides";
import { MARKETPLACE_OG_IMAGE, MARKETPLACE_OG_IMAGE_ALT, SITE_URL, buildPageMetadata } from "@/lib/seo";
import { useT } from "@/i18n";

export const Route = createFileRoute("/compare/marketplace/")({
  head: () =>
    buildPageMetadata({
      path: "/compare/marketplace",
      title: "Marketplace Alternatives — BeHot.Now vs Fiverr, Upwork and More",
      description:
        "Sourced comparisons between the BeHot.Now Marketplace and Fiverr, Upwork, Contra, SoundBetter, AirGigs, PeoplePerHour, Kwork and Freelancer.com: fees, visibility and payouts.",
      ogTitle: "BeHot.Now Marketplace vs other service marketplaces",
      ogDescription:
        "Side by side comparisons of creator and freelance marketplaces: publishing costs, commission, paid visibility, verification and payouts.",
      image: MARKETPLACE_OG_IMAGE,
      imageAlt: MARKETPLACE_OG_IMAGE_ALT,
      keywords: [
        "fiverr alternative",
        "upwork alternative",
        "creator marketplace comparison",
        "freelance marketplace fees compared",
      ],
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Marketplace", path: "/marketplace" },
        { name: "Comparisons", path: "/compare/marketplace" },
      ],
      jsonLd: {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "BeHot.Now Marketplace comparisons",
        url: `${SITE_URL}/compare/marketplace`,
        hasPart: MP_COMPARISONS.map((c) => ({
          "@type": "WebPage",
          name: c.h1,
          url: `${SITE_URL}/compare/marketplace/${c.slug}`,
          description: c.description,
        })),
      },
    }),
  component: MpCompareHub,
});

function MpCompareHub() {
  const { t } = useT();
  return (
    <div className="mx-auto max-w-4xl px-4 py-10">
      <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary">
        <Scale className="size-3.5" /> {t("mpseo.comparisons")}
      </span>
      <h1 className="mt-3 font-display text-3xl font-bold sm:text-4xl">{t("mpseo.chub.h1")}</h1>
      <p className="mt-4 max-w-2xl leading-relaxed text-muted-foreground">{t("mpseo.chub.intro")}</p>

      <div className="mt-8 grid gap-3 sm:grid-cols-2">
        {MP_COMPARISONS.map((c) => (
          <Link
            key={c.slug}
            to="/compare/marketplace/$competitor"
            params={{ competitor: c.slug }}
            className="card-surface group p-4 transition hover:border-primary/50"
          >
            <h2 className="font-display font-bold">{c.h1}</h2>
            <p className="mt-1.5 text-sm text-muted-foreground">{c.description}</p>
            <span className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-primary">
              {t("mpseo.read")} <ArrowRight className="size-4" />
            </span>
          </Link>
        ))}
      </div>

      <section className="mt-12">
        <h2 className="font-display text-sm font-bold uppercase tracking-widest text-muted-foreground">
          {t("mpseo.keepReading")}
        </h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {MP_GUIDES.slice(0, 6).map((g) => (
            <Link
              key={g.slug}
              to="/marketplace/guides/$slug"
              params={{ slug: g.slug }}
              className="rounded-full border border-border bg-card px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground"
            >
              {g.h1}
            </Link>
          ))}
        </div>
      </section>

      <div className="mt-10 flex flex-wrap gap-3">
        <Button asChild variant="hot">
          <Link to="/marketplace">{t("mpseo.cta.open")}</Link>
        </Button>
        <Button asChild variant="outline">
          <Link to="/compare/hot">{t("mpseo.cta.compareBoards")}</Link>
        </Button>
      </div>
    </div>
  );
}
